import httpService from "./htttp.service";

class AccountService {
  async getUserAccounts(userId) {
    try {
      if (!userId) {
        throw new Error("User ID is required");
      }

      const response = await httpService.get(`users/${userId}/bank-accounts`);
      
      if (!response || !response.accounts) {
        throw new Error("Invalid response format from server");
      }

      if (!Array.isArray(response.accounts)) {
        throw new Error("Accounts data is not in the expected format");
      }

      return response.accounts;
    } catch (error) {
      console.error("Error fetching user accounts:", error);
      
      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        switch (error.response.status) {
          case 401:
            throw new Error("Authentication failed. Please login again.");
          case 403:
            throw new Error("You don't have permission to access these accounts.");
          case 404:
            throw new Error("User or accounts not found.");
          case 500:
            throw new Error("Server error. Please try again later.");
          default:
            throw new Error(error.response.data?.message || "Failed to fetch accounts");
        }
      } else if (error.request) {
        // The request was made but no response was received
        throw new Error("No response from server. Please check your connection.");
      } else {
        // Something happened in setting up the request that triggered an Error
        throw new Error(error.message || "An unexpected error occurred");
      }
    }
  }

  async createAccount(userId, accountData) {
    try {
      if (!userId) {
        console.error("No userId provided");
        throw new Error("User ID is required");
      }

      const response = await httpService.post(`users/${userId}/bank-accounts`, accountData);

      if (!response) {
        throw new Error("Invalid response from server");
      }

      return response;
    } catch (error) {
      console.error("Error creating account. Full error:", error);
      console.error("Error response:", error.response);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);
      
      // Handle specific error cases
      if (error.response) {
        switch (error.response.status) {
          case 401:
            throw new Error("Authentication failed. Please login again.");
          case 403:
            throw new Error("You don't have permission to create accounts.");
          case 400:
            throw new Error("Invalid account data. Please check your input.");
          case 500:
            throw new Error("Server error. Please try again later.");
          default:
            throw new Error(error.response.data?.message || "Failed to create account");
        }
      } else if (error.request) {
        throw new Error("No response from server. Please check your connection.");
      } else {
        throw new Error(error.message || "An unexpected error occurred");
      }
    }
  }

  async addBankAccountReading(accountId, balance) {
    try {
      const response = await httpService.post(
        `bank-accounts/${accountId}/bank-account-readings`,
        { balance }

      );

      if (!response) {
        throw new Error("Invalid response from server");
      }

      return response;
    } catch (error) {
      console.error("Error adding bank reading:", error);
      throw error;
    }
  }

  async getBankAccountReadings(id) {
    try {
      const response = await httpService.get(`bank-accounts/${id}/bank-account-readings`);
      
      if (!response || !response.bankAccountReadings) {
        throw new Error("Invalid response format from server");
      }

      if (!Array.isArray(response.bankAccountReadings)) {
        throw new Error("Readings data is not in the expected format");
      }

      return response;
    } catch (error) {
      console.error("Error fetching bank account readings:", error);
      
      // Handle specific error cases
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        switch (error.response.status) {
          case 401:
            throw new Error("Authentication failed. Please login again.");
          case 403:
            throw new Error("You don't have permission to access these readings.");
          case 404:
            throw new Error("User or readings not found.");
          case 500:
            throw new Error("Server error. Please try again later.");
          default:
            throw new Error(error.response.data?.message || "Failed to fetch readings");
        }
      } else if (error.request) {
        // The request was made but no response was received
        throw new Error("No response from server. Please check your connection.");
      } else {
        // Something happened in setting up the request that triggered an Error
        throw new Error(error.message || "An unexpected error occurred");
      }
    }
  }

  async deleteAccount(userId, id) {
    try {
      if (!userId) {
        throw new Error("User ID is required");
      }
      if (!id) {
        throw new Error("Account ID is required");
      }

      const response = await httpService.delete(`users/${userId}/bank-accounts/${id}`);
      
      // For 204 No Content, we don't need to check the response content
      // Just return success if we get here (no error was thrown)
      return { success: true };
    } catch (error) {
      console.error("Error deleting account:", error);
      
      if (error.response) {
        switch (error.response.status) {
          case 401:
            throw new Error("Authentication failed. Please login again.");
          case 403:
            throw new Error("You don't have permission to delete this account.");
          case 404:
            throw new Error("Account not found.");
          case 500:
            throw new Error("Server error. Please try again later.");
          default:
            throw new Error(error.response.data?.message || "Failed to delete account");
        }
      } else if (error.request) {
        throw new Error("No response from server. Please check your connection.");
      } else {
        throw new Error(error.message || "An unexpected error occurred");
      }
    }
  }
}

const accountService = new AccountService();
export default accountService; 